/* eslint-disable max-classes-per-file */
import { Platform } from 'react-native-web';
import { colors } from '@styleseat/brand';
import ng from '../../../modules/angularThings';

import analytics from '../../../modules/analytics';

import stateManager from '../../../modules/user/stateManager';

import { SecurityLockCircleIcon } from '../../ui/Icons/SecurityLockCircleIcon';

import { Subscription, SubscriptionBannerKey } from '../../../modules/provider/Subscription';
import { BodyText } from '../../ui';

/**
 * Class representing short subscription banners
 * used for display in the Daily, Calendar, and Clients tabs.
 */
class ShortSubscriptionBanner {
  background: string;

  foreground: string;

  bannerSource: string;

  cta: string;

  header: string;

  iosApp: boolean;

  subscription: Subscription;

  tracking_key: string;

  weight: string;

  icon: React.ReactNode;

  iconColor: string;

  style: React.CSSProperties;

  appearance: React.ComponentProps<typeof BodyText>['appearance'];

  constructor(subscription: Subscription, bannerSource: string) {
    const responsiveService = ng.$injector.get('responsiveService');
    this.background = colors.chill;
    this.bannerSource = bannerSource;
    this.cta = null;
    this.foreground = colors.blackInBlack;
    this.header = null;
    this.iosApp = responsiveService.iosApp;
    this.subscription = subscription;
    this.tracking_key = 'base';
    this.weight = 'bold';
    this.icon = null;
    this.iconColor = null;
    this.style = null;
    this.appearance = null;
  }

  action() {
    const $state = ng.$injector.get('$state');

    analytics.track('P Clicked Subscribe Banner', {
      source: this.bannerSource,
      past_due: this.subscription.requiresPaymentUpdate,
    });

    $state.go('responsive.provider.subscription', {
      providerId: this.subscription.provider_id,
      utm_medium: 'banner',
      utm_campaign: 'short_banners',
    });
  }
}

/**
 * Class representing the banner content displayed when
 * a pro is early (less than a week) into their trial.
 * @extends ShortSubscriptionBanner
 */
class EarlyTrialBanner extends ShortSubscriptionBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    if (this.iosApp) {
      this.header = 'Visit StyleSeat\'s website to update your account.';
      this.tracking_key = 'visit_website';
    } else {
      this.header = 'Welcome to StyleSeat!';
      this.cta = 'Subscribe Now';
      this.tracking_key = 'subscription_early_trial';
    }
  }
}

/**
 * Class representing the banner content displayed when
 * a pro is late (atleast week) into their trial.
 * @extends ShortSubscriptionBanner
 */
class LateTrialBanner extends ShortSubscriptionBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    if (this.iosApp) {
      this.header = 'Visit StyleSeat\'s website to update your account.';
      this.tracking_key = 'visit_website';
    } else {
      this.header = `Only ${this.subscription.daysRemaining} left!`;
      this.cta = 'Subscribe Now';
      this.tracking_key = 'subscription_late_trial';
    }
  }
}

/*
 * Class representing the banner content displayed when
 * a pro's trial has expired.
 * @extends ShortSubscriptionBanner
 */
class TrialExpiredBanner extends ShortSubscriptionBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.background = colors.red;
    this.foreground = colors.whiteWash;
    if (this.iosApp) {
      this.header = 'Your account needs attention.';
      this.tracking_key = 'account_needs_attention';
    } else {
      this.header = 'Your free trial has ended.';
      this.tracking_key = 'subscription_trial_expired';
      this.cta = 'Subscribe Now';
    }
  }
}

/**
 * Class representing the banner content displayed when
 * a pro is in their grace period.
 * @extends ShortSubscriptionBanner
 */
class GracePeriodBanner extends ShortSubscriptionBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.background = colors.red;
    this.foreground = colors.whiteWash;
    if (this.iosApp) {
      this.header = 'Your account needs attention.';
      this.tracking_key = 'subscription_account_needs_attention';
    } else {
      this.header = 'Subscription Payment Declined';
      this.tracking_key = 'subscription_payment_declined';
      this.cta = 'Update';
    }
  }
}

/**
 * Class representing the banner content displayed when
 * a pro is canceled, but still active on StyleSeat.
 * @extends ShortSubscriptionBanner
 */
class CanceledAndNotExpiredBanner extends ShortSubscriptionBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.background = colors.red;
    this.foreground = colors.whiteWash;
    this.header = 'Your account is cancelled.';
    this.tracking_key = 'subscription_account_is_cancelled';
    if (!this.iosApp) {
      this.cta = 'Renew Now';
    }
  }
}

/**
 * Class representing the banner content displayed when
 * a provider is still pending payments verification.
 * @extends ShortSubscriptionBanner
 */
class PendingPaymentsVerificationBanner extends ShortSubscriptionBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.foreground = colors.blackInBlack;
    this.header = "We're verifying your details to charge on StyleSeat.";
    this.cta = 'Learn More';
    this.tracking_key = 'verification_details';
  }

  // Override ShortSubscriptionBanner -> action()
  static action() {
    analytics.track('pro_verification_details_banner_learn_more_clicked');
    if (Platform.OS === 'web') {
      window.open('https://www.styleseat.com/blog/how-to-charge-clients-on-styleseat', '_system');
    } else {
      // Not implemented
    }
  }
}

/**
 * Class representing the banner content displayed when
 * a provider should be told we don't share their data.
 * @extends ShortSubscriptionBanner
 */
class ClientsSecurityBanner extends ShortSubscriptionBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.background = colors.itsOff;
    this.foreground = colors.metal;
    this.header = 'Your clients’ information is safe in StyleSeat.\nWe never share or sell it.';
    this.tracking_key = 'clients_security';
    this.icon = SecurityLockCircleIcon;
    this.iconColor = colors.ash;
    this.style = {
      padding: '12px',
    };
    this.appearance = 'small';
  }

  static dismiss() {
    const $state = ng.$injector.get('$state');

    analytics.track('pro_clients_security_banner_dismissed');
    stateManager.addAction('pro_clients_security_banner_dismissed', true);
    $state.go('.', {}, { reload: true });
  }
}

/** Verification Banner
 * Base class for banners related to Stripe Verification errors
 */
class VerificationBanner extends ShortSubscriptionBanner {
  routeName: string;

  routeParams: Record<string, any>;

  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.background = colors.red;
    this.foreground = colors.whiteWash;
    this.header = 'Additional details required in order to charge on StyleSeat.';
    this.cta = 'Add Now';
    this.tracking_key = 'verification_error';
    this.routeName = 'provider.settings_menu.payments';
    this.routeParams = {};
  }

  // Override ShortSubscriptionBanner -> action()
  action() {
    analytics.track(`pro_${this.tracking_key}_banner_cta_clicked`);
    const $state = ng.$injector.get('$state');
    // go the the verification details window.
    $state.go(this.routeName, this.routeParams, { reload: true });
  }
}

/**
 * Class representing the banner content displayed when
 * a provider needs to provider full SSN for payments verification.
 * @extends VerificationBanner
 */
class FullSSNRequiredBanner extends VerificationBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.tracking_key = 'full_ssn_required';
    this.routeParams = { openVerificationDetails: true };
  }
}

/**
 * Class representing the banner content displayed when
 * a provider needs to provide a legal document for payments verification.
 * @extends VerificationBanner
 */
class LegalDocumentRequiredBanner extends VerificationBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.tracking_key = 'legal_document_required';
    this.routeParams = { openVerificationDetails: true };
  }
}

/**
 * Class representing the banner content displayed when
 * a provider needs to provide a valid mailing address for payments verification
 * @extends VerificationBanner
 */
class MailingAddressRequiredBanner extends VerificationBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.tracking_key = 'mailing_address_required';
    this.routeParams = { openMailingAddress: true };
  }
}

/**
 * Class representing the banner content displayed when
 * a provider needs to provide a valid EIN for payments verification
 * @extends VerificationBanner
 */
class CompanyEinRequiredBanner extends VerificationBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.tracking_key = 'company_ein_required';
    this.routeParams = { openVerificationDetails: true };
  }
}

class GeneralUnverifiedBanner extends VerificationBanner {
  constructor(subscription: Subscription, bannerSource: string) {
    super(subscription, bannerSource);
    this.tracking_key = 'unverified';
    this.routeParams = { openVerificationDetails: true };
  }
}

export default {
  [SubscriptionBannerKey.EarlyTrialBanner]: EarlyTrialBanner,
  [SubscriptionBannerKey.LateTrialBanner]: LateTrialBanner,
  [SubscriptionBannerKey.TrialExpiredBanner]: TrialExpiredBanner,
  [SubscriptionBannerKey.GracePeriodBanner]: GracePeriodBanner,
  [SubscriptionBannerKey.CanceledAndNotExpiredBanner]: CanceledAndNotExpiredBanner,
  [SubscriptionBannerKey.FullSSNRequiredBanner]: FullSSNRequiredBanner,
  [SubscriptionBannerKey.LegalDocumentRequiredBanner]: LegalDocumentRequiredBanner,
  [SubscriptionBannerKey.MailingAddressRequiredBanner]: MailingAddressRequiredBanner,
  [SubscriptionBannerKey.CompanyEINRequiredBanner]: CompanyEinRequiredBanner,
  [SubscriptionBannerKey.GeneralUnverifiedBanner]: GeneralUnverifiedBanner,
  [SubscriptionBannerKey.PendingPaymentsVerificationBanner]: PendingPaymentsVerificationBanner,
  [SubscriptionBannerKey.ClientsSecurityBanner]: ClientsSecurityBanner,
};
